import { ServicePageTemplate } from "@/components/ServicePageTemplate";
import { Mail, RefreshCw, TrendingUp, Users, BarChart3, Clock, Cpu, Star, Megaphone } from "lucide-react";

const features = [
  {
    icon: Mail,
    title: "Email Campaigns",
    description: "Professional email sequences to past customers with seasonal offers, maintenance reminders, and special promotions.",
  },
  {
    icon: RefreshCw,
    title: "SMS Campaigns",
    description: "High-open-rate SMS messages that reach past customers directly. Perfect for time-sensitive offers and reactivation.",
  },
  {
    icon: Users,
    title: "Database Segmentation",
    description: "We segment your past customer list by service type, location, and job value to send the right message to the right people.",
  },
  {
    icon: Clock,
    title: "Automated Sequences",
    description: "Set-and-forget reactivation sequences that run on autopilot — bringing in repeat business without lifting a finger.",
  },
  {
    icon: TrendingUp,
    title: "ROI Tracking",
    description: "Track exactly which campaigns bring back customers and generate revenue. Full transparency on campaign performance.",
  },
  {
    icon: BarChart3,
    title: "Performance Optimisation",
    description: "We test, refine, and optimise every campaign to improve open rates, click rates, and booking conversions.",
  },
];

const benefits = [
  "Turn past customers into repeat revenue",
  "Highest ROI marketing channel — they already trust you",
  "Automated sequences run on autopilot",
  "Seasonal campaigns for maintenance and upgrades",
  "SMS campaigns with 98% open rates",
  "Segmented targeting for personalised messaging",
  "Fill quiet periods with reactivation campaigns",
  "Build long-term customer relationships",
];

export default function Reactivation() {
  return (
    <ServicePageTemplate
      title="Reactivation Campaigns"
      subtitle="Win back past customers with smart email & SMS campaigns. High ROI from your existing database."
      metaTitle="Reactivation Campaigns for Tradies | FullScale Marketing"
      metaDescription="Email and SMS reactivation campaigns for Australian tradies. Turn past customers into repeat revenue with automated campaigns."
      canonicalUrl="https://fullscalemarketing.com.au/services/reactivation"
      badgeText="Reactivation"
      icon={Mail}
      introText="Your past customers are your most valuable asset. They already know your work, trust your team, and are far more likely to book again than a cold lead. Our reactivation campaigns bring past customers back with perfectly timed email and SMS campaigns. For maximum impact, combine reactivation with automation tools and reputation management to re-engage, follow up, and collect reviews — all automatically."
      features={features}
      benefits={benefits}
      ctaHeading="Ready to Turn Past Customers Into New Revenue?"
      ctaText="Book a free strategy call and we'll show you how reactivation campaigns can generate high-ROI revenue from your existing database."
      relatedServices={[
        {
          icon: Cpu,
          title: "Automation & AI Tools",
          description: "Automate your reactivation sequences and follow-ups for a fully hands-free system.",
          href: "/services/automation",
        },
        {
          icon: Star,
          title: "Reputation & Reviews",
          description: "Re-engage past customers to leave reviews while bringing them back for repeat work.",
          href: "/services/reputation",
        },
        {
          icon: Megaphone,
          title: "Lead Generation Ads",
          description: "Combine reactivation with new lead generation for consistent pipeline growth.",
          href: "/services/lead-generation",
        },
      ]}
      faqs={[
        {
          question: "How many past customers do I need for this to work?",
          answer: "Even a list of 50–100 past customers can generate significant results. The larger your database, the more revenue potential — but we've seen strong ROI from smaller lists too.",
        },
        {
          question: "What kind of offers work best for reactivation?",
          answer: "Seasonal maintenance reminders, loyalty discounts, upgrade offers, and 'we haven't heard from you' check-ins all perform well. We'll recommend the right approach based on your trade and customer base.",
        },
        {
          question: "Will my customers find the messages annoying?",
          answer: "Not at all. Our campaigns are professionally written, well-timed, and always include an easy opt-out. Most customers appreciate the reminder — especially for maintenance services they've been putting off.",
        },
        {
          question: "Can you run campaigns for me on an ongoing basis?",
          answer: "Yes. We can set up automated sequences that run continuously, or manage monthly campaigns for you. Many clients do both — automated nurture plus seasonal promotions.",
        },
        {
          question: "How do you measure ROI on reactivation campaigns?",
          answer: "We track open rates, click rates, replies, and booked jobs from every campaign. You'll see exactly how many past customers came back and the revenue generated from each campaign.",
        },
      ]}
    />
  );
}
